import React, { useState, useEffect, useRef } from 'react';
import {
  Table, Button, Modal, Form, Alert, Spinner, Image,
  Row,
  Col,
  InputGroup
} from 'react-bootstrap';
import { FaTrashAlt, FaEdit, FaSearch } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from "react-router-dom";
import { adminsAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import LocationAutocomplete from "../../components/LocationAutocomplete";
import { useSearchFilter } from '../useSearchFilter';

function AdminCompany() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileRef = useRef(null); // Add ref for file input

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [editingId, setEditingId] = useState(null);
  const [passwordError, setPasswordError] = useState("");
  const [photoFile, setPhotoFile] = useState(null); // Add state for file
  const [searchTerm, setSearchTerm] = useState('');
  const filteredData = useSearchFilter(data, searchTerm, ['email']);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    city: '',
    type: '',
    phone: '',
    age: '',
    postal_code: '',
    gender: '',
    is_super: '0',
    password: '',
    confirm_password: '',
    photo: '' // This will store the URL for display
  });

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const handleLocationChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
      ...(e.lat && { [`${e.target.name}_lat`]: e.lat }),
      ...(e.lng && { [`${e.target.name}_lng`]: e.lng })
    }));
  };

  // Fetch admins data
  const fetchAdmins = async (page = 1) => {
    try {
      setLoading(true);
      setError("");
      const response = await adminsAPI.index({
        page,
        per_page: pagination.per_page,
      });

      if (response.success) {
        setData(response.data.data || []);
        setPagination(response.data.meta || {
          current_page: page,
          last_page: 1,
          per_page: 10,
          total: response.data.data.length,
        });
      } else {
        setError(response.message || t("failed_to_fetch_data"));
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("failed_to_fetch_data"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      setError(t("failed_to_fetch_data"));
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_to_fetch_data"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error fetching admins:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      setPhotoFile(file); // Store the file object
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({ ...prev, photo: reader.result })); // Store preview URL
        };
        reader.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    // Clear password error when user types
    if (name === 'password' || name === 'confirm_password') {
      setPasswordError("");
    }
  };

  // Reset file input
  const resetFileInput = () => {
    setPhotoFile(null);
    setFormData(prev => ({ ...prev, photo: '' }));
    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };

  // Open modal for add
  const openAddModal = () => {
    setModalType('add');
    setFormData({
      name: '',
      email: '',
      address: '',
      city: '',
      type: '',
      phone: '',
      age: '',
      postal_code: '',
      gender: '',
      is_super: '0',
      password: '',
      confirm_password: '',
      photo: ''
    });
    setPhotoFile(null); // Reset file
    if (fileRef.current) fileRef.current.value = '';
    setShowModal(true);
    setPasswordError("");
  };

  // Open modal for edit
  const openEditModal = (admin) => {
    setModalType('edit');
    setEditingId(admin.id);
    setFormData({
      name: admin.name || '',
      email: admin.email || '',
      address: admin.address || '',
      city: admin.city || '',
      type: admin.type || '',
      phone: admin.phone || '',
      age: admin.age || '',
      postal_code: admin.postal_code || '',
      gender: admin.gender || '',
      is_super: admin.is_super?.toString() || '0',
      password: '',
      confirm_password: '',
      photo: admin.photo || '' // Set to URL for display
    });
    setPhotoFile(null); // Reset file
    if (fileRef.current) fileRef.current.value = '';
    setShowModal(true);
    setPasswordError("");
  };

  // Handle form submission (add/edit)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords
    if (modalType === 'add' && (!formData.password || formData.password.length < 8)) {
      setPasswordError(t("password_must_be_at_least_8_characters"));
      return;
    }
    if (formData.password !== formData.confirm_password) {
      setPasswordError(t("passwords_do_not_match"));
      return;
    }

    try {
      setLoading(true);
      setError("");

      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        // Skip password and confirm_password if empty during edit
        if (modalType === 'edit' && (key === 'password' || key === 'confirm_password') && !formData[key]) {
          return;
        }
        if (key === 'photo' && photoFile) {
          formDataToSend.append('photo', photoFile); // Append file if exists
        } else if (key !== 'photo' && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      let response;
      if (modalType === 'add') {
        response = await adminsAPI.store(formDataToSend);
      } else {
        response = await adminsAPI.update(editingId, formDataToSend);
      }

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: modalType === 'add' ? t("admin_added_successfully") : t("admin_updated_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowModal(false);
        fetchAdmins(pagination.current_page);
      } else {
        setError(response.message || t("operation_failed"));
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("operation_failed"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      setError(t("operation_failed"));
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("operation_failed"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error submitting form:", err);
    } finally {
      setLoading(false);
    }
  };

  // Confirm delete
  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await adminsAPI.delete(deleteId);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("admin_deleted_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowDelete(false);
        fetchAdmins(pagination.current_page);
      } else {
        setError(response.message || t("delete_failed"));
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("delete_failed"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      setError(t("delete_failed"));
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("delete_failed"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error deleting admin:", err);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="fw-bold mb-0">👤 {t('Admins List')}</h3>
        <Button className='add-btn' onClick={openAddModal} disabled={loading}>
          ➕ {t('Add Admin')}
        </Button>
      </div>

      {/* Search Bar */}
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={t('search_email')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button 
                variant="outline-secondary" 
                onClick={clearSearch}
                title={t("reset_search")}
              >
                ✕
              </Button>
            )}
          </InputGroup>
        </Col>
      </Row>
      
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : data.length === 0 ? (
        <Alert variant="info">{t('no_data_available')}</Alert>
      ) : (
        <>
          <div className="table-container shadow-sm rounded">
            <div className='table-responsive'>
              <Table bordered hover className="custom-table mb-0 text-center">
                <thead className="table-dark">
                  <tr>
                    <th>{t('ID')}</th>
                    <th>{t('Photo')}</th>
                    <th>{t('Name')}</th>
                    <th>{t('Email')}</th>
                    <th>{t('Address')}</th>
                    <th>{t('city')}</th>
                    <th>{t('Type')}</th>
                    <th>{t('Phone')}</th>
                    <th>{t('age')}</th>
                    <th>{t('postal_code')}</th>
                    <th>{t('gender')}</th>
                    <th>{t('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.id}</td>
                      <td>
                        {entry.photo ? (
                          <Image
                            src={entry.photo}
                            alt={entry.name}
                            width={50}
                            height={50}
                            roundedCircle
                            style={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <span>{t('no_picture')}</span>
                        )}
                      </td>
                      <td>{entry.name}</td>
                      <td>
                        {searchTerm ? (
                          <span 
                            dangerouslySetInnerHTML={{
                              __html: entry.email?.replace(
                                new RegExp(searchTerm, 'gi'), 
                                match => `<mark class="bg-warning">${match}</mark>`
                              )
                            }} 
                          />
                        ) : (
                          entry.email
                        )}
                      </td>
                      <td>{entry.address}</td>
                      <td>{entry.city}</td>
                      <td>{entry.is_super === 1 ? t('Super Admin') : t('Admin')}</td>
                      <td>{entry.phone}</td>
                      <td>{entry.age}</td>
                      <td>{entry.postal_code}</td>
                      <td>{entry.gender}</td>
                      <td>
                        <Button
                          className="add-btn me-2"
                          size="sm"
                          onClick={() => openEditModal(entry)}
                          title={t('Edit')}
                          disabled={loading}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setDeleteId(entry.id);
                            setShowDelete(true);
                          }}
                          title={t('Delete')}
                          disabled={loading}
                        >
                          <FaTrashAlt />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>

          {pagination.total > 0 && (
            <PaginationComponent
              currentPage={pagination.current_page}
              lastPage={pagination.last_page}
              onPageChange={fetchAdmins}
              disabled={loading}
            />
          )}
        </>
      )}

      {/* Delete Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirm_deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('confirm_delete_admin')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)} disabled={loading}>
            {t('Cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? t('Deleting...') : t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === 'add' ? t('Add New Admin') : t('Edit Admin')}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {passwordError && <Alert variant="danger">{passwordError}</Alert>}

            {modalType === 'edit' && formData.photo && (
              <Form.Group className="mb-3">
                <Form.Label>{t('Current Photo')}</Form.Label>
                <div>
                  <Image
                    src={formData.photo}
                    alt="current_admin_photo"
                    width={100}
                    height={100}
                    rounded
                    style={{ objectFit: 'cover' }}
                  />
                  <br/>
                  {photoFile && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={resetFileInput}
                      disabled={loading}
                      className="mt-2"
                    >
                      {t('Cancel Photo')}
                    </Button>
                  )}
                </div>
              </Form.Group>
            )}

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('Name')} *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('Email')} *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('phone')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('age')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('Address')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('city')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3" controlId="formPostalCode">
                  <Form.Label>{t('postal_code')}</Form.Label>
                  <LocationAutocomplete
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleLocationChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('gender')}</Form.Label>
                  <Form.Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('Select Gender')}</option>
                    <option value="Male">{t('Male')}</option>
                    <option value="Female">{t('Female')}</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('Photo')}</Form.Label>
                  <Form.Control
                    type="file"
                    name="photo"
                    ref={fileRef}
                    onChange={handleInputChange}
                    accept="image/*"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('Type')}</Form.Label>
                  <Form.Select
                    name="is_super"
                    value={formData.is_super}
                    onChange={handleInputChange}
                  >
                    <option value="0">{t('Admin')}</option>
                    <option value="1">{t('Super Admin')}</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>
                    {modalType === 'add' ? t('Password') + ' *' : t('New Password')}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={modalType === 'add'}
                    placeholder={modalType === 'edit' ? t('Leave blank to keep current password') : ''}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>
                    {modalType === 'add' ? t('Confirm Password') + ' *' : t('Confirm New Password')}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    required={modalType === 'add'}
                    placeholder={modalType === 'edit' ? t('Leave blank to keep current password') : ''}
                  />
                </Form.Group>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('Saving...') : t('save')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default AdminCompany;